import React from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  useLocation,
  matchPath,
} from "react-router-dom";

import userRole from "config/userRole";
import useAuth from "hooks/useAuth";

// Styles
import "assets/styles/theme.css";
import "assets/styles/print.css";
import "assets/styles/tooltip-overrides.css";
import "config/themeInit";

// modules
import Dashboard from "views/Dashboard";
import Unloads from "views/Unloads";
import Loads from "views/Loads";
import Doors from "views/Doors";
import Individuals from "views/Individuals";
import Admin from "views/Admin";
import Settings from "views/Settings";
import Login from "views/Login";
import Monitor from "views/Monitor";
import Btc from 'views/Btc';
import Notifications from "./views/Notifications";
import Process from "./views/Process";
import Reports from "./views/Reports";
import DCMap from "./views/DCMap";
import Carts from "./views/Carts";
import TrackPageView from "components/TrackPageView";

const {
  pages: { DashboardPage },
} = Dashboard;

const {
  pages: { SettingsPage, SchedulePage },
} = Settings;

const {
  pages: { LoginPage, ForgotPasswordPage, ResetPasswordPage },
} = Login;

const {
  pages: {
    IndividualsActivePage,
    IndividualsCheckedPage,
    IndividualsStatsPage,
    IndividualDetails,
  },
} = Individuals;

const {
  pages: { UnloadStatsPage, UnloadStatsBIPage, ContainerDetailsPage },
} = Unloads;

const {
  pages: { LoadStatsPage, LoadStatsBIPage, TrailerDetailsPage },
} = Loads;

const {
  pages: { DoorDetailsPage },
} = Doors;

const {
  pages: {
    UsersPage,
    LocationsPage,
    DoorsPage,
    EmployeesPage,
    ServersPage,
    CamerasPage,
    BtcAdminPage,
    AgenciesPage,
    FeatureFlagsPage,
  },
} = Admin;

const {
  pages: { MonitorPage },
} = Monitor;
const {
  pages: { BtcPage },
} = Btc;
const {
  pages: { NotificationsPage },
} = Notifications;

const {
  pages: { ProcessDetailsPage },
} = Process;

const {
  pages: { ReportsPage },
} = Reports;

const {
  pages: { DCMapPage },
} = DCMap;

const {
  pages: { CartsStatsPage, CartsFlowPage, CartDetailsPage },
} = Carts;

const Routes = () => {
  const auth = useAuth();

  return (
    <Router>
       <TrackPageView />
      <Switch>
        <MonitorRoute exact path={MonitorPage.path} component={MonitorPage} />
        <MonitorRoute exact path={BtcPage.path} component={BtcPage} />
        <PublicRoute exact path={LoginPage.path} component={LoginPage} />
        <PublicRoute
          exact
          path={ForgotPasswordPage.path}
          component={ForgotPasswordPage}
        />
        <PublicRoute
          exact
          path={ResetPasswordPage.path}
          component={ResetPasswordPage}
        />

        <ProtectedRoute
          exact
          path={ResetPasswordPage.protectedPath}
          component={ResetPasswordPage}
        />
        <ProtectedRoute
          exact
          path={DashboardPage.path}
          component={DashboardPage}
        />
        <ProtectedRoute
          exact
          path={UnloadStatsPage.path}
          component={UnloadStatsPage}
        />
        <ProtectedRoute
          exact
          path={UnloadStatsBIPage.path}
          component={UnloadStatsBIPage}
        />
        <ProtectedRoute
          exact
          path={LoadStatsPage.path}
          component={LoadStatsPage}
        />
        <ProtectedRoute
          exact
          path={LoadStatsBIPage.path}
          component={LoadStatsBIPage}
        />
        <ProtectedRoute
          exact
          path={DoorDetailsPage.path}
          component={DoorDetailsPage}
        />
        <ProtectedRoute
          exact
          path={TrailerDetailsPage.path}
          component={TrailerDetailsPage}
        />
        <ProtectedRoute
          exact
          path={ContainerDetailsPage.path}
          component={ContainerDetailsPage}
        />
        <ProtectedRoute
          exact
          path={IndividualsActivePage.path}
          component={IndividualsActivePage}
        />
        <ProtectedRoute
          exact
          path={IndividualsCheckedPage.path}
          component={IndividualsCheckedPage}
        />
        <ProtectedRoute
          exact
          path={IndividualsStatsPage.path}
          component={IndividualsStatsPage}
        />
        <ProtectedRoute
          exact
          path={IndividualDetails.path}
          component={IndividualDetails}
        />
        <ProtectedRoute
          exact
          path={SettingsPage.path}
          component={SettingsPage}
        />
        <ProtectedRoute
          exact
          path={SchedulePage.path}
          component={SchedulePage}
        />
        <ProtectedRoute
          exact
          path={NotificationsPage.path}
          component={NotificationsPage}
        />
        <ProtectedRoute exact path={ReportsPage.path} component={ReportsPage} />
        <ProtectedRoute
          exact
          path={CartsStatsPage.path}
          component={CartsStatsPage}
        />
        <ProtectedRoute
          exact
          path={CartsFlowPage.path}
          component={CartsFlowPage}
        />
        <ProtectedRoute
          exact
          path={CartDetailsPage.path}
          component={CartDetailsPage}
        />

        <ProtectedRoute exact path={DCMapPage.path} component={DCMapPage} />

        <AdminRoute exact path={UsersPage.path} component={UsersPage} />
        <AdminRoute exact path={LocationsPage.path} component={LocationsPage} />
        <AdminRoute exact path={DoorsPage.path} component={DoorsPage} />
        <AdminRoute exact path={EmployeesPage.path} component={EmployeesPage} />
        <AdminRoute exact path={CamerasPage.path} component={CamerasPage} />
        <AdminRoute exact path={BtcAdminPage.path} component={BtcAdminPage} />
        <AdminRoute exact path={ServersPage.path} component={ServersPage} />
        <AdminRoute exact path={AgenciesPage.path} component={AgenciesPage} />
        <AdminRoute exact path={FeatureFlagsPage.path} component={FeatureFlagsPage} />
        <AdminRoute
          exact
          path={ProcessDetailsPage.path}
          component={ProcessDetailsPage}
        />

        <Route
          path="/logout"
          render={({ history }) => {
            auth.actions.logout().then(() => {
              history.push(LoginPage.path);
            });
          }}
        />
        <Redirect path="/" to={DashboardPage.path} />
      </Switch>
    </Router>
  );
};

const PublicRoute = (props) => {
  const { state: auth } = useAuth();

  if (auth.isAuthenticated) {
    return <Redirect to={DashboardPage.path} />;
  }

  return <Route {...props} />;
};

const MonitorRoute = (props) => {
  const { state: auth } = useAuth();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const hw_id = params.get("hw_id");
  const apiKey = params.get("apiKey");

  if (auth.isAuthenticated && auth.currentUser.role === userRole.AGENCY) {
    return <Redirect to={IndividualsStatsPage.path} />;
  }

  if (!auth.isAuthenticated && (hw_id || apiKey)) {
    return <Route {...props} />;
  }

  if (!auth.isAuthenticated) {
    return <Redirect to={LoginPage.path} />;
  }

  return <Route {...props} />;
};

const ProtectedRoute = (props) => {
  const { state: auth } = useAuth();
  const location = useLocation();

  if (!auth.isAuthenticated) {
    return <Redirect to={LoginPage.path} />;
  }

  if (
    auth.requiresPassReset &&
    location.pathname !== ResetPasswordPage.protectedPath
  ) {
    return <Redirect to={ResetPasswordPage.protectedPath} />;
  }

  if (auth.currentUser.role === userRole.AGENCY) {
    const allowedPaths = [
      IndividualsStatsPage.path,
      IndividualDetails.path,
      ReportsPage.path,
      SettingsPage.path,
    ];
    let isMatch = false;
    allowedPaths.forEach((path) => {
      isMatch |= !!matchPath(location.pathname, {
        path,
        exact: true,
      });
    });
    if (!isMatch) {
      return <Redirect to={IndividualsStatsPage.path} />;
    }
  }

  return <Route {...props} />;
};

// @deprecated
// in favor of feature toggles
// const DevOnlyProtectedRoute = (props) => {
//   if (!env.isDev) {
//     return <Redirect to={DashboardPage.path} />;
//   }

//   return (
//     <ProtectedRoute {...props} />
//   );
// };

const AdminRoute = (props) => {
  const { state: auth } = useAuth();

  if (!auth.isAuthenticated) {
    return <Redirect to={LoginPage.path} />;
  }

  if (auth.currentUser.role !== userRole.ADMIN) {
    return <Redirect to={DashboardPage.path} />;
  }

  return <Route {...props} />;
};

export default Routes;